import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  requestReviewEditOtp,
  updateReviewViaToken,
  verifyReviewEditOtp,
} from './api';
import { EditReviewScene } from './scene';

export default function EditReview() {
  const navigate = useNavigate();
  const [step, setStep] = useState<EditReview.Step>('contact');
  const [contact, setContact] = useState('');
  const [code, setCode] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Kudable.EditableReview[]>([]);
  const [editing, setEditing] = useState<Kudable.EditableReview | null>(null);
  const [overallRating, setOverallRating] = useState(0);
  const [comment, setComment] = useState('');
  const [improvementSuggestion, setImprovementSuggestion] = useState('');
  const [improvementPublic, setImprovementPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedProfileToken, setSavedProfileToken] = useState<string | null>(
    null,
  );

  const requestOtp = () => {
    if (!contact.trim()) return;
    setBusy(true);
    setError(null);
    requestReviewEditOtp(contact.trim())
      .then(() => setStep('otp'))
      .catch(() => setError('Could not send code. Please try again.'))
      .finally(() => setBusy(false));
  };

  const verifyOtp = () => {
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    verifyReviewEditOtp(contact.trim(), code.trim())
      .then((response) => {
        setToken(response.data.token);
        setReviews(response.data.reviews);
        if (response.data.reviews.length === 0) {
          setError('No reviews found for this contact.');
          setStep('contact');
        } else {
          setStep('list');
        }
      })
      .catch(() => setError('Invalid or expired code. Please try again.'))
      .finally(() => setBusy(false));
  };

  const startEdit = (review: Kudable.EditableReview) => {
    setEditing(review);
    setOverallRating(review.overall_rating);
    setComment('');
    setImprovementSuggestion('');
    setImprovementPublic(false);
    setStep('edit');
  };

  const saveEdit = () => {
    if (!token || !editing || overallRating === 0) return;
    setBusy(true);
    setError(null);
    const trimmedSuggestion = improvementSuggestion.trim();
    updateReviewViaToken(editing.id, token, {
      overall_rating: overallRating,
      comment: comment.trim() || editing.comment || null,
      improvement_suggestion:
        trimmedSuggestion || editing.improvement_suggestion || null,
      improvement_public: trimmedSuggestion
        ? improvementPublic
        : (editing.improvement_public ?? false),
    })
      .then((response) => {
        setSavedProfileToken(
          response.data.review.profile_token || editing.profile_token || null,
        );
        setStep('done');
      })
      .catch(() =>
        setError('Could not save changes. The session may have expired.'),
      )
      .finally(() => setBusy(false));
  };

  const startOver = () => {
    setStep('contact');
    setContact('');
    setCode('');
    setToken(null);
    setReviews([]);
    setEditing(null);
    setError(null);
  };

  return (
    <EditReviewScene
      step={step}
      contact={contact}
      code={code}
      reviews={reviews}
      editing={editing}
      overallRating={overallRating}
      comment={comment}
      improvementSuggestion={improvementSuggestion}
      improvementPublic={improvementPublic}
      busy={busy}
      error={error}
      onContactChange={setContact}
      onCodeChange={setCode}
      onRequestOtp={requestOtp}
      onVerifyOtp={verifyOtp}
      onStartEdit={startEdit}
      onOverallRatingChange={setOverallRating}
      onCommentChange={setComment}
      onImprovementSuggestionChange={setImprovementSuggestion}
      onImprovementPublicChange={setImprovementPublic}
      onSaveEdit={saveEdit}
      onStartOver={startOver}
      onGoToProfile={
        savedProfileToken
          ? () => navigate(`/p/${savedProfileToken}`)
          : undefined
      }
    />
  );
}
